// Page ID Utilities for Debugging
import React, { useEffect, useState } from 'react';

/** Browser crypto-based randomness (no Math.random); exported for client IDs elsewhere. */
function getCrypto(): Crypto | undefined {
  return typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
}

export function randomClientIdSuffix(): string {
  const c = getCrypto();
  if (c?.randomUUID) return c.randomUUID();
  const a = new Uint32Array(2);
  c?.getRandomValues(a);
  return `${a[0].toString(16)}${a[1].toString(16)}`;
}

export function randomInt31(): number {
  const c = getCrypto();
  const u = new Uint32Array(1);
  c?.getRandomValues(u);
  return u[0]! & 0x7fffffff;
}

/** Uniform float in [0, 1). */
export function randomUnitFloat(): number {
  const c = getCrypto();
  const u = new Uint32Array(1);
  c?.getRandomValues(u);
  return u[0]! / 4294967296;
}

export function randomAlphanumeric(len: number): string {
  const alphabet = '0123456789abcdefghijklmnopqrstuvwxyz';
  const c = getCrypto();
  const u = new Uint8Array(len);
  c?.getRandomValues(u);
  let s = '';
  for (let i = 0; i < len; i++) s += alphabet[u[i]! % alphabet.length]!;
  return s;
}

// Generate a unique page ID
export const generatePageId = (componentName: string): string => {
  const timestamp = Date.now();
  const random = randomAlphanumeric(6);
  return `${componentName}_${timestamp}_${random}`;
};

// Hook to log page ID on component mount
export const usePageId = (componentName: string, additionalInfo?: string) => {
  useEffect(() => {
    const pageId = generatePageId(componentName);
    console.log(`Page ID: ${pageId} - ${new Date().toISOString()}`);
    if (additionalInfo) {
      console.log(`Additional Info: ${additionalInfo}`);
    }
  }, [componentName, additionalInfo]);
};

// Component wrapper for automatic page ID logging
export const withPageId = <P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) => {
  const WrappedComponent = (props: P) => {
    usePageId(componentName);
    return React.createElement(Component, props);
  };
  
  WrappedComponent.displayName = `withPageId(${componentName})`;
  return WrappedComponent;
};

// Global state for page ID visibility - Hidden by default
let globalPageIdVisible = false;
const pageIdVisibilityListeners: ((visible: boolean) => void)[] = [];

export const setPageIdVisible = (visible: boolean) => {
  globalPageIdVisible = visible;
  pageIdVisibilityListeners.forEach(listener => listener(visible));
};

export const getPageIdVisible = () => globalPageIdVisible;

// Hook for PageId visibility state
export const usePageIdVisibility = () => {
  const [visible, setVisible] = useState(globalPageIdVisible);
  
  useEffect(() => {
    const listener = (newVisible: boolean) => setVisible(newVisible);
    pageIdVisibilityListeners.push(listener);
    
    return () => {
      const index = pageIdVisibilityListeners.indexOf(listener);
      if (index > -1) {
        pageIdVisibilityListeners.splice(index, 1);
      }
    };
  }, []);
  
  return visible;
};

// Debug info component with toggle functionality
export const PageIdDisplay: React.FC<{ componentName: string; additionalInfo?: string }> = ({ 
  componentName, 
  additionalInfo 
}) => {
  const pageId = generatePageId(componentName);
  const visible = usePageIdVisibility();
  
  if (!visible) return null;
  
  return (
    <div className="fixed bottom-4 left-4 bg-black bg-opacity-75 text-white text-xs p-2 rounded z-50 font-mono max-w-xs">
      <div>🔍 {pageId}</div>
      <div>📍 {componentName}</div>
      {additionalInfo && <div>ℹ️ {additionalInfo}</div>}
    </div>
  );
};

// Toggle button component for PageId visibility
export const PageIdToggle: React.FC = () => {
  const visible = usePageIdVisibility();
  
  return (
    <button
      onClick={() => setPageIdVisible(!visible)}
      className="fixed bottom-4 right-4 bg-gray-800 hover:bg-gray-700 text-white text-xs px-3 py-2 rounded-full z-50 font-mono transition-colors duration-200 shadow-lg"
      title={visible ? 'Hide Page ID' : 'Show Page ID'}
    >
      {visible ? '🔍👁️' : '🔍👁️‍🗨️'}
    </button>
  );
}; 